/**
 * "You have documents waiting on your acknowledgment" — §7.7's nudge.
 *
 * **This is why auto-enrolment works from the person's side.** §7.7 requires a
 * new hire to enrol into an open cycle by existing, and the notification cannot
 * carry that: it is emitted once, when the cycle opens, to whoever the audience
 * resolved to at that instant. Somebody provisioned afterwards is in the
 * denominator and was never told. This banner is a live query instead, so there
 * is no moment for it to miss.
 *
 * **Dismissal is a nudge's dismissal, not a record's.** It is keyed to the set of
 * requests currently outstanding, so hiding it says "not now" about these, and a
 * new cycle brings it back. It is client-side and per-browser on purpose: the
 * banner is not the obligation, and nothing here should look like discharging
 * one. What the obligation actually is lives on `/acknowledgments`, and what it
 * is worth to the studio lives on the compliance view — neither of which can be
 * dismissed.
 *
 * Overdue items are named separately, because "three waiting" and "three
 * waiting, one past its date" are different sentences to the person reading them.
 */

import { Link } from '@tanstack/react-router';
import { XClose } from '@untitledui/icons';
import { type ReactElement, useEffect, useState } from 'react';
import { useAcknowledgmentHistory } from '../api/queries.ts';

const STORAGE_KEY = 'matchbox.acknowledgments.dismissed';

/**
 * The identity of "these ones", so a dismissal survives a reload and does not
 * survive a new cycle. Sorted, because the server's order is not a promise.
 */
function signatureOf(requestIds: readonly string[]): string {
  return [...requestIds].sort().join(',');
}

function readDismissed(): string {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    // Safari in private mode throws on access rather than returning null. A
    // banner that cannot remember a dismissal is worse company than one that
    // crashes the shell it sits in.
    return '';
  }
}

function writeDismissed(signature: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, signature);
  } catch {
    // As above: the dismissal is lost, the page is not.
  }
}

function sentence(total: number, overdue: number): string {
  const noun = total === 1 ? 'document is' : 'documents are';
  if (overdue === 0) return `${total} ${noun} waiting on your acknowledgment.`;
  return `${total} ${noun} waiting on your acknowledgment — ${overdue} past the date.`;
}

export function AcknowledgmentBanner(): ReactElement | null {
  const query = useAcknowledgmentHistory();
  const [dismissed, setDismissed] = useState<string>('');

  // Read once on mount rather than during render: `localStorage` is not state
  // React knows about, and reading it inline makes the first paint depend on it.
  useEffect(() => {
    setDismissed(readDismissed());
  }, []);

  const outstanding = query.data?.outstanding ?? [];
  if (outstanding.length === 0) return null;

  const signature = signatureOf(outstanding.map((item) => item.requestId));
  if (signature === dismissed) return null;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-warning-primary bg-warning-primary px-4 py-3">
      <p className="min-w-0 flex-1 text-sm text-primary">
        {sentence(outstanding.length, query.data?.overdue ?? 0)}{' '}
        <Link to="/acknowledgments" className="font-medium link">
          Review what you owe
        </Link>
      </p>
      <button
        type="button"
        aria-label="Dismiss until the next acknowledgment"
        className="shrink-0 rounded p-1 text-tertiary hover:text-primary"
        onClick={() => {
          writeDismissed(signature);
          setDismissed(signature);
        }}
      >
        <XClose className="size-4" />
      </button>
    </div>
  );
}
